import dayjs from 'dayjs'
import { convertToFullPeriod, toBelgianTime } from 'utils/date.ts'
import { FullPeriodPrice } from 'components/charts/price-history/types.ts'
import { GenerateFullPeriodPrices } from './types.ts'

/**
 * Maps the given prices onto every period between from & until, so the chart always covers the entire selected period.
 * Periods without a price (e.g. the part of the current period that is still in the future) get a null price, keeping
 * their axis label without drawing a bar.
 *
 * @param {GenerateFullPeriodPrices} params
 * @returns {FullPeriodPrice[]}
 */
export const generateFullPeriodPrices = ({ prices, from, until, granularity }: GenerateFullPeriodPrices): FullPeriodPrice[] => {
  /**
   * The getPriceHistory request covers more than the shown period (see PriceHistory), so leave out everything outside it.
   * Periods starting after today are dropped as well: the day-ahead market already knows tomorrow's prices, but showing
   * them would suggest the period is complete. convertToFullPeriod adds them back as empty entries.
   */
  const endOfToday = toBelgianTime().endOf('day')
  const pricesInPeriod = prices.filter(
    ({ date }) => !dayjs(date).isBefore(from) && !dayjs(date).isAfter(until) && !dayjs(date).isAfter(endOfToday)
  )

  return convertToFullPeriod<FullPeriodPrice>({
    data: pricesInPeriod,
    from,
    until,
    granularity,
    getDate: ({ date }) => date,
    createEmptyEntry: (date) => ({ date, price: null })
  })
}
